import { Box, Container, useTheme, Theme, Stack, Divider } from "@mui/material";
import { BottomBarActionItems } from "./BottomBarActionItems";
import { BottomBarEvents } from "./BottomBarEvents";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  AvailableCredit,
  AvailableCreditLogin,
  TicketSubtotal,
} from "./SubtotalElements";
import { useFrontendSubtotals } from "./useFrontendSubtotals";
import { useAppStore } from "@/global/store";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import { selectUser } from "@/global/store/data/user";
import { purchaseSeatsTabUIDs } from "@/global/config/constants";
import { useRouter } from "next/router";
import { GameSelectionProgress } from "./GameSelectionProgress";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import {
  selectOrderServiceFee,
  selectServiceFeeTotal,
} from "@/global/store/data/purchaseSeats/serviceFee";
import { useBackendSubtotals } from "./useBackendSubtotlals";
import { useBuckets } from "../Buckets/useBuckets";
import { usePacks } from "../Packs/usePacks";

export function BottomBar() {
  const { md: mdDown, lg: lgDown } = useCssMediaQuery("down");
  if (mdDown) return <MobileBottomBar />;
  if (lgDown) return <TabletBottomBar />;
  else return <DesktopBottomBar />;
}

function MobileBottomBar() {
  const router = useRouter();
  const theme = useTheme();
  const { sm: smDown } = useCssMediaQuery("down");
  const styles = useStyles(theme);
  const teamConfig = useTeamConfig();
  const { availableCreditEnabled } = teamConfig;
  const { showGameSelectionProgress } = teamConfig.uiConfigs.bottomBar;
  const selectedEvents = useAppStore(selectEventsSelected);
  const frontendSubtotals = useFrontendSubtotals();
  const backendSubtotals = useBackendSubtotals();
  const user = useAppStore(selectUser);
  const purchaseSeatTab = router.query.purchaseSeatTab as string;
  const orderServiceFee = useAppStore(selectOrderServiceFee);
  const serviceFeeTotalAmount = useAppStore(selectServiceFeeTotal);
  const { isBucketsEnabledAndMobile } = useBuckets();
  const { isPackBucketsEnabledAndMobile } = usePacks();

  if (
    purchaseSeatTab == purchaseSeatsTabUIDs.checkout ||
    purchaseSeatTab == purchaseSeatsTabUIDs.chooseSeats
  ) {
    return (
      <Box sx={styles.bottomBar}>
        <Box py={2}>
          <Container maxWidth="lg">
            <Stack direction={"row"} justifyContent={"space-between"}>
              <BottomBarEvents maxVisibleIconCount={3} />
              <BottomBarActionItems />
            </Stack>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.bottomBar}>
      {(showGameSelectionProgress &&
        purchaseSeatTab == purchaseSeatsTabUIDs.findGames &&
        !(isBucketsEnabledAndMobile || isPackBucketsEnabledAndMobile) &&
        !smDown) ||
      (showGameSelectionProgress &&
        availableCreditEnabled &&
        purchaseSeatTab == purchaseSeatsTabUIDs.findGames) ? (
        <>
          <Box p={2}>
            <GameSelectionProgress />
          </Box>
          <Divider />
        </>
      ) : null}

      {selectedEvents.length > 0 ? (
        <>
          <Box py={2}>
            <Container maxWidth="lg">
              <BottomBarEvents maxVisibleIconCount={15} />
            </Container>
          </Box>
          <Divider />
        </>
      ) : null}

      {frontendSubtotals.ticketSubtotal > 0 ||
      serviceFeeTotalAmount > 0 ||
      orderServiceFee > 0 ? (
        <>
          <Box py={2}>
            <Container>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <TicketSubtotal />
              </Stack>
            </Container>
          </Box>
          <Divider />
        </>
      ) : null}

      <Box py={2}>
        <Container>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={1}
          >
            {availableCreditEnabled && user !== null ? (
              <Box>
                <AvailableCredit amount={backendSubtotals.availableCredit} />
              </Box>
            ) : null}

            {availableCreditEnabled &&
            (purchaseSeatTab == purchaseSeatsTabUIDs.findGames ||
              purchaseSeatTab == purchaseSeatsTabUIDs.packages) &&
            !user ? (
              <Box>
                <AvailableCreditLogin />
              </Box>
            ) : null}

            <BottomBarActionItems />
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

function TabletBottomBar() {
  const router = useRouter();
  const theme = useTheme();
  const styles = useStyles(theme);
  const teamConfig = useTeamConfig();
  const { availableCreditEnabled } = teamConfig;
  const { showGameSelectionProgress } = teamConfig.uiConfigs.bottomBar;
  const selectedEvents = useAppStore(selectEventsSelected);
  const backendSubtotals = useBackendSubtotals();
  const user = useAppStore(selectUser);
  const purchaseSeatTab = router.query.purchaseSeatTab as string;

  return (
    <Box sx={styles.bottomBar}>
      {selectedEvents.length > 0 ? (
        <>
          <Box py={2}>
            <Container maxWidth="lg">
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                spacing={2}
              >
                <BottomBarEvents maxVisibleIconCount={15} />
                {showGameSelectionProgress &&
                purchaseSeatTab == purchaseSeatsTabUIDs.findGames ? (
                  <GameSelectionProgress />
                ) : null}
              </Stack>
            </Container>
          </Box>
          <Divider />
        </>
      ) : null}

      <Box py={2}>
        <Container>
          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <TicketSubtotal />

              {availableCreditEnabled && user !== null ? (
                <AvailableCredit amount={backendSubtotals.availableCredit} />
              ) : null}

              {availableCreditEnabled &&
              (purchaseSeatTab == purchaseSeatsTabUIDs.findGames ||
                purchaseSeatTab == purchaseSeatsTabUIDs.packages) &&
              !user ? (
                <AvailableCreditLogin />
              ) : null}
            </Stack>

            <BottomBarActionItems />
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

function DesktopBottomBar() {
  const router = useRouter();
  const theme = useTheme();
  const styles = useStyles(theme);
  const teamConfig = useTeamConfig();
  const { availableCreditEnabled } = teamConfig;
  const { showGameSelectionProgress } = teamConfig.uiConfigs.bottomBar;
  const selectedEvents = useAppStore(selectEventsSelected);
  const frontendSubtotals = useFrontendSubtotals();
  const backendSubtotals = useBackendSubtotals();
  const user = useAppStore(selectUser);
  const purchaseSeatTab = router.query.purchaseSeatTab as string;

  return (
    <Box sx={styles.bottomBar} py={3}>
      <Container maxWidth="xl">
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"flex-start"}
          spacing={2}
        >
          {showGameSelectionProgress &&
          purchaseSeatTab == purchaseSeatsTabUIDs.findGames ? (
            <GameSelectionProgress />
          ) : null}
          <Box>
            {selectedEvents.length > 0 ? (
              <BottomBarEvents maxVisibleIconCount={15} />
            ) : null}
          </Box>
          <Box flexGrow={1} />
          <Stack direction={"row"} spacing={2} alignItems={"center"}>
            {frontendSubtotals.ticketSubtotal > 0 ? (
              <>
                <TicketSubtotal />
              </>
            ) : null}

            {availableCreditEnabled && user !== null ? (
              <>
                <AvailableCredit amount={backendSubtotals.availableCredit} />
              </>
            ) : null}

            {availableCreditEnabled &&
            (purchaseSeatTab == purchaseSeatsTabUIDs.findGames ||
              purchaseSeatTab == purchaseSeatsTabUIDs.packages) &&
            !user ? (
              <AvailableCreditLogin />
            ) : null}

            <BottomBarActionItems />
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function useStyles(theme: Theme) {
  return {
    bottomBar: {
      position: "sticky",
      bottom: 0,
      left: 0,
      width: "100%",
      backgroundColor: theme.palette.background.paper,
      borderTop: `1px solid ${theme.palette.divider}`,
      zIndex: 10,
    },

    divider: {
      height: "64px",
    },
  };
}
